import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import '../styles/AdminPage.css';
import '../styles/TagSorterPage.css';

// Keep in sync with TagClassification.tier (models.py) and DEFAULT_TIER (mood_routes.py) - the
// mood picker treats "Unsorted" the same as "Specific" until an admin actually sorts a tag.
const TIERS = [
  { id: 'unsorted', label: 'Unsorted', hint: 'Not reviewed yet' },
  { id: 1, label: 'Main', hint: 'Well-known & broad, e.g. Mystery, Drama, Sci-Fi' },
  { id: 2, label: 'Secondary', hint: 'Slightly more specific, e.g. Liminal Horror, Aliens' },
  { id: 3, label: 'Specific', hint: 'Oddly specific, e.g. Nuclear Power Plant, Skateboarding' },
  { id: 4, label: 'Minor', hint: 'Less relevant, e.g. Based on Toy, After-Credits Stinger' },
];

const MEDIA_TYPE_LABELS = {
  ScreenMedia: 'TV & Movies',
  SteamGame: 'Steam Games',
  PhysicalGame: 'Physical Games',
};

const TagSorterPage = ({ apiBaseUrl }) => {
  const { isAuthenticated, userRoles } = useAuth();
  const isAdmin = isAuthenticated && userRoles.includes('admin');

  const [tagsByType, setTagsByType] = useState({});
  const [activeType, setActiveType] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [dragOverTier, setDragOverTier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${apiBaseUrl}/admin/tags`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // Guard against unexpected shapes (e.g. an error payload) so the page can't crash on it.
        const clean = {};
        Object.entries(data || {}).forEach(([type, tags]) => {
          if (Array.isArray(tags)) clean[type] = tags;
        });
        setTagsByType(clean);
        const firstType = Object.keys(clean).sort()[0];
        setActiveType((prev) => prev || firstType || null);
      })
      .catch((err) => console.error('Failed to load tags:', err))
      .finally(() => setLoading(false));
  }, [apiBaseUrl, isAdmin]);

  const tierOf = (tag) => {
    const list = Array.isArray(tagsByType[activeType]) ? tagsByType[activeType] : [];
    const entry = list.find((t) => t.tag.toLowerCase() === tag.toLowerCase());
    return entry?.tier ?? 'unsorted';
  };

  const columns = useMemo(() => {
    const tags = Array.isArray(tagsByType[activeType]) ? tagsByType[activeType] : [];
    const filtered = filterText.trim()
      ? tags.filter((t) => t.tag.toLowerCase().includes(filterText.trim().toLowerCase()))
      : tags;
    const grouped = {};
    TIERS.forEach((tier) => { grouped[tier.id] = []; });
    filtered.forEach((t) => {
      const tier = t.tier ?? 'unsorted';
      (grouped[tier] || grouped.unsorted).push(t);
    });
    return grouped;
  }, [tagsByType, activeType, filterText]);

  const moveTag = async (tag, newTier) => {
    const oldTier = tierOf(tag);
    if (oldTier === newTier) return;

    // Optimistic update across every media type this tag appears in - tiers are global.
    setTagsByType((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((type) => {
        next[type] = next[type].map((t) =>
          t.tag.toLowerCase() === tag.toLowerCase() ? { ...t, tier: newTier === 'unsorted' ? null : newTier } : t
        );
      });
      return next;
    });

    try {
      const res = await fetch(`${apiBaseUrl}/admin/tags/classify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tag, tier: newTier === 'unsorted' ? null : newTier }),
      });
      if (!res.ok) throw new Error('Request failed');
    } catch (err) {
      console.error('Failed to classify tag, reverting:', err);
      setTagsByType((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((type) => {
          next[type] = next[type].map((t) =>
            t.tag.toLowerCase() === tag.toLowerCase() ? { ...t, tier: oldTier === 'unsorted' ? null : oldTier } : t
          );
        });
        return next;
      });
    }
  };

  const handleDragStart = (e, tag) => {
    e.dataTransfer.setData('text/plain', tag);
  };

  const handleDrop = (e, tierId) => {
    e.preventDefault();
    setDragOverTier(null);
    const tag = e.dataTransfer.getData('text/plain');
    if (tag) moveTag(tag, tierId);
  };

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="home-container">
        <h1 className="admin-title">Access Denied</h1>
        <div className="admin-tile">
          <p>You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const mediaTypes = Object.keys(tagsByType).sort();

  return (
    <div className="home-container tag-sorter-container">
      <h1 className="admin-title">Tag Sorter</h1>
      <p className="tag-sorter-intro">
        Drag tags between columns to set how strongly they should count as a "mood" for the
        Feeling Lucky / Feeling Adventurous picker. This has no effect anywhere else.
      </p>

      {loading && <p>Loading tags…</p>}

      {!loading && (
        <>
          <div className="tag-sorter-tabs">
            {mediaTypes.map((type) => (
              <button
                key={type}
                type="button"
                className={`tag-sorter-tab${type === activeType ? ' active' : ''}`}
                onClick={() => setActiveType(type)}
              >
                {MEDIA_TYPE_LABELS[type] || type}
              </button>
            ))}
          </div>

          <input
            type="text"
            className="tag-sorter-filter"
            placeholder="Filter tags…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />

          <div className="tag-sorter-columns">
            {TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`tag-sorter-column${dragOverTier === tier.id ? ' drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverTier(tier.id); }}
                onDragLeave={() => setDragOverTier((prev) => (prev === tier.id ? null : prev))}
                onDrop={(e) => handleDrop(e, tier.id)}
              >
                <h2 className="tag-sorter-column-title">{tier.label}</h2>
                <p className="tag-sorter-column-hint">{tier.hint}</p>
                <div className="tag-sorter-chip-list">
                  {(columns[tier.id] || []).map((t) => (
                    <div
                      key={t.tag}
                      className="tag-sorter-chip"
                      draggable
                      onDragStart={(e) => handleDragStart(e, t.tag)}
                      title={`${t.tag} (used by ${t.count} item${t.count === 1 ? '' : 's'})`}
                    >
                      {t.tag}
                      <span className="tag-sorter-chip-count">{t.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TagSorterPage;
